import type { AngebotStatus, AuftragStatus, Rechnung,
              RechnungAnzeigeStatus } from "@kundeninfo/api-client";
import type { StatusArt } from "@kundeninfo/ui";

/**
 * Die deutschen Beschriftungen der Vorgangsstatus — die EINE Fassung im System.
 *
 * Sie standen bis zum Paket „Menü komplett" je einmal in KundeAngebote.tsx und
 * KundeAuftraege.tsx. Mit den GLOBALEN Listen (heute die Objektlisten in
 * views/BereichAngebote.tsx und views/BereichAuftraege.tsx) brauchte sie ein zweiter
 * Bildschirm — und zwar doppelt: für die Statusspalte einer Zeile
 * UND für die Auswahlliste des Statusfilters. Zwei Tabellen bedeuteten zwei Gelegenheiten,
 * dass derselbe Status an zwei Bildschirmen verschieden heisst, und eine Stelle, an der
 * ein neu hinzugekommener Status vergessen wird. Deshalb stehen sie hier, aus demselben
 * Grund wie `formatBetrag` in format.ts.
 *
 * Die Statusunionen kommen aus dem Klienten (`AngebotStatus`/`AuftragStatus`, aus der
 * Trefferzeile gezogen) — nicht abgetippt: Ein neuer Statuswert im Vertrag macht diese
 * Tabelle unvollständig, und das meldet der Typprüfer, bevor irgendwo eine rohe Konstante
 * wie „WITHDRAWN" auf dem Bildschirm landet.
 *
 * Die Reihenfolge der Schlüssel ist der Lebenslauf des Vorgangs; die Filterauswahl der
 * Bereichs-Filterköpfe übernimmt sie unverändert (`Object.keys`).
 */
export const ANGEBOT_STATUS_LABEL: Record<AngebotStatus, string> = {
  DRAFT: "Entwurf",
  SENT: "Gesendet",
  ACCEPTED: "Angenommen",
  REJECTED: "Abgelehnt",
  WITHDRAWN: "Zurückgezogen",
  // Abgelöst durch eine neuere Version (Paket Angebotsversionen). Er steht hier, weil
  // die Beschriftungstabelle ERSCHÖPFEND sein muss — in den Bereichs-Listen taucht er
  // trotzdem nie auf: Sie zeigen je Reihe nur die jüngste Version, und der Statusfilter
  // (`ANGEBOTS_FILTER`, bereichsfilter.ts) führt bewusst KEINEN Chip dafür.
  SUPERSEDED: "Veraltet",
};

export const AUFTRAG_STATUS_LABEL: Record<AuftragStatus, string> = {
  ACTIVE: "Aktiv",
  COMPLETED: "Abgeschlossen",
  CANCELLED: "Storniert",
};

/**
 * Die RECHNUNG (Bereichs-Spalten, Etappe 1, Aufgabe 5b) — seit ihr Bereich eine
 * eigene Liste hat, braucht auch sie zwei Bildschirme: die Zeile der Liste und die
 * Kopfzeile des Bausteins (components/RechnungDetail.tsx). Deshalb steht sie hier und
 * nicht länger als lokale Tabelle im Baustein.
 *
 * Ein ENTWURF heisst bewusst NICHT noch einmal „Entwurf": Das steht schon an der Stelle
 * der Nummer, die er noch nicht hat — die Statusstelle sagt stattdessen, was fehlt.
 *
 * Die Statusunion kommt aus dem Klienten (`Rechnung["status"]`) und ist nicht abgetippt;
 * einen benannten Alias gibt es dort — anders als bei Angebot und Auftrag — nicht.
 */
export const RECHNUNG_STATUS_LABEL: Record<Rechnung["status"], string> = {
  DRAFT: "noch nicht festgeschrieben",
  ISSUED: "Festgeschrieben",
  CANCELLED: "Storniert",
};

/**
 * Die vier ANZEIGE-Gruppen der Rechnungsliste (Paket „Rechnungsstatus", Migration 018) —
 * die Beschriftungen der Status-Filterliste und der Chips im Rechnungs-Bereich.
 *
 * **Eine zweite Tabelle neben `RECHNUNG_STATUS_LABEL`, und das ist Absicht**: Die beiden
 * beantworten verschiedene Fragen. `RECHNUNG_STATUS_LABEL` beschriftet den Beleg SELBST
 * (im Baustein, wo daneben „Bezahlt am …" als eigene Angabe steht) — dort heisst ein
 * Entwurf „noch nicht festgeschrieben", weil an der Stelle der Nummer schon „Entwurf"
 * steht. Diese Tabelle beschriftet die GRUPPE, in der eine Zeile steht; dort ist „Entwurf"
 * genau das richtige Wort, und „Bezahlt" ist eine eigene Gruppe, die es am Beleg gar nicht
 * gibt. Eine gemeinsame Tabelle müsste an beiden Stellen dasselbe Wort benutzen und wäre
 * an einer davon falsch.
 *
 * Die Statusunion kommt aus dem Klienten (`RechnungAnzeigeStatus`, aus der Trefferzeile
 * gezogen) und ist nicht abgetippt: Ein neuer Wert im Vertrag macht diese Tabelle
 * unvollständig, und das meldet der Typprüfer.
 *
 * Die Reihenfolge der Schlüssel ist der Lebenslauf; die Filterliste übernimmt sie
 * unverändert (`Object.keys`).
 */
export const RECHNUNG_ANZEIGE_LABEL: Record<RechnungAnzeigeStatus, string> = {
  DRAFT: "Entwurf",
  ISSUED: "Festgeschrieben",
  PAID: "Bezahlt",
  CANCELLED: "Storniert",
};

/**
 * Die STATUS-ART je Vorgangsstatus (Chip `statusArt`, Zeilenstatus der Objektliste) — wie die Beschriftungen die EINE Fassung im
 * System: Bereichsliste und Kopfzeile in der Akte zeigen denselben Status gleich (Hausregel Dirk 02.11.2026, Entscheid 671; bis
 * dahin hatte die Akte eigene Pillen mit eigener Farbwahl). Gesendet, aktiv und festgeschrieben laufen (eine offene Sache,
 * Ruling 23.09.2026), angenommen, abgeschlossen und bezahlt sind gut, abgelehnt ist schlecht, zurückgezogen und storniert sind
 * Warnungen, Entwurf und Veraltet sind noch bzw. nicht mehr etwas. Als `Record` über den Statustyp: Ein neuer Status meldet sich
 * im Typprüfer, statt ungefärbt zu bleiben.
 */
export const ANGEBOT_STATUS_ART: Record<AngebotStatus, StatusArt> = {
  DRAFT: "aus", SENT: "laeuft", ACCEPTED: "ok", REJECTED: "fehler", WITHDRAWN: "warnung", SUPERSEDED: "aus",
};

export const AUFTRAG_STATUS_ART: Record<AuftragStatus, StatusArt> = { ACTIVE: "laeuft", COMPLETED: "ok", CANCELLED: "warnung" };

/** Die Gruppe der Rechnungsliste: `ISSUED` und `PAID` sind verschieden eingefärbt — in „Alle Rechnungen“ ist genau das die Frage. */
export const RECHNUNG_ANZEIGE_ART: Record<RechnungAnzeigeStatus, StatusArt> = { DRAFT: "aus", ISSUED: "laeuft", PAID: "ok", CANCELLED: "warnung" };

/** Der Beleg selbst (Kopfzeile in der Akte): dieselben Arten wie seine Gruppe; „Bezahlt am …“ steht dort als eigene Angabe. */
export const RECHNUNG_STATUS_ART: Record<Rechnung["status"], StatusArt> = { DRAFT: "aus", ISSUED: "laeuft", CANCELLED: "warnung" };
